/**
 * Individual terminal panel component.
 * Manages a single xterm.js instance with WebSocket connection.
 *
 * @module components/terminal/TerminalPanel
 */

import { useEffect, useRef, useCallback, useMemo } from 'react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { RefreshCw } from 'lucide-react';
import { useExecStream, type ConnectionState } from '@/hooks';
import { useTerminalStore } from './useTerminalStore';
import { getTerminalContent, createTerminalKeyHandler } from './utils';
import type { TerminalPanelProps } from './types';
import '@xterm/xterm/css/xterm.css';

/**
 * Single terminal panel with xterm.js integration.
 * Can be either the main panel or a split panel within a tab.
 */
export function TerminalPanel({
  sandboxId,
  containerName,
  sandboxGroupId,
  tabId,
  panelType,
  isActive,
}: TerminalPanelProps) {
  const terminalRef = useRef<HTMLDivElement>(null);
  const xtermRef = useRef<Terminal | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const connectionStateRef = useRef<ConnectionState>('disconnected');
  const isInitializedRef = useRef(false);
  const execStreamRef = useRef<ReturnType<typeof useExecStream> | null>(null);

  // Get store actions based on panel type
  const updateConnectionState = useTerminalStore((s) =>
    panelType === 'main' ? s.updateTabConnectionState : s.updateSplitConnectionState
  );
  const updateSessionId = useTerminalStore((s) =>
    panelType === 'main' ? s.updateTabSessionId : s.updateSplitSessionId
  );
  const registerTerminalContentGetter = useTerminalStore((s) => s.registerTerminalContentGetter);
  const unregisterTerminalContentGetter = useTerminalStore(
    (s) => s.unregisterTerminalContentGetter
  );

  // Subscribe to this panel's connection state for reactive updates
  const panelConnectionState = useTerminalStore(
    useCallback(
      (s) => {
        const tab = s.tabs.find((t) => t.id === tabId);
        if (!tab) return 'disconnected';
        if (panelType === 'main') {
          return tab.connectionState;
        } else {
          return tab.split?.connectionState ?? 'disconnected';
        }
      },
      [tabId, panelType]
    )
  );

  // Unique key for this panel (used in logs)
  const panelKey = `${tabId}:${panelType}`;

  // Memoize callbacks to prevent unnecessary recreations
  const handleStateChange = useCallback(
    (state: ConnectionState) => {
      console.log(`[TerminalPanel:${panelKey}] State changed to:`, state);
      connectionStateRef.current = state;
      updateConnectionState(tabId, state);

      if (xtermRef.current) {
        if (state === 'connected') {
          xtermRef.current.writeln('\x1b[32m[Connected to shell]\x1b[0m');
        } else if (state === 'connecting') {
          xtermRef.current.writeln('\x1b[33m[Connecting...]\x1b[0m');
        } else if (state === 'error') {
          xtermRef.current.writeln('\x1b[31m[Connection error]\x1b[0m');
          // Terminal stays visible - user can reconnect manually via button
        }
      }
    },
    [tabId, panelKey, updateConnectionState]
  );

  const handleSessionId = useCallback(
    (id: string) => {
      console.log(`[TerminalPanel:${panelKey}] Session ID:`, id);
      updateSessionId(tabId, id);
      if (xtermRef.current) {
        xtermRef.current.writeln(`\x1b[90m[Session ID: ${id}]\x1b[0m`);
        // Re-send terminal dimensions to ensure PTY size is synchronized.
        // The PTY starts with default 80x24 and our initial dimensions
        // travel through multiple proxy layers, so they may arrive late.
        const rows = xtermRef.current.rows;
        const cols = xtermRef.current.cols;
        if (execStreamRef.current) {
          console.log(
            `[TerminalPanel:${panelKey}] Syncing dimensions after session start: ${rows}x${cols}`
          );
          execStreamRef.current.resize(rows, cols);
        }
      }
    },
    [tabId, panelKey, updateSessionId]
  );

  const handleOutput = useCallback((data: string) => {
    if (xtermRef.current) {
      xtermRef.current.write(data);
    }
  }, []);

  const handleExit = useCallback(
    (exitCode: number) => {
      console.log(`[TerminalPanel:${panelKey}] Process exited with code:`, exitCode);
      if (xtermRef.current) {
        xtermRef.current.writeln(`\r\n\x1b[33m[Process exited with code ${exitCode}]\x1b[0m`);
      }
    },
    [panelKey]
  );

  const handleError = useCallback(
    (error: string) => {
      console.error(`[TerminalPanel:${panelKey}] Error:`, error);
      if (xtermRef.current) {
        xtermRef.current.writeln(`\r\n\x1b[31m[Error: ${error}]\x1b[0m`);
      }
    },
    [panelKey]
  );

  const handleReconnecting = useCallback(
    (attempt: number, maxAttempts: number) => {
      console.log(`[TerminalPanel:${panelKey}] Reconnecting attempt ${attempt}/${maxAttempts}`);
      if (xtermRef.current) {
        xtermRef.current.writeln(
          `\r\n\x1b[33m[Connection lost. Reconnecting... attempt ${attempt}/${maxAttempts}]\x1b[0m`
        );
      }
    },
    [panelKey]
  );

  const handleReconnectFailed = useCallback(() => {
    console.log(`[TerminalPanel:${panelKey}] Reconnection failed after all attempts`);
    if (xtermRef.current) {
      xtermRef.current.writeln(
        `\r\n\x1b[31m[Reconnection failed. Click "Reconnect" to try again.]\x1b[0m`
      );
    }
    // Terminal stays visible - user can reconnect manually via button
  }, [panelKey]);

  // Memoize exec stream options to prevent hook from reinitializing
  const execStreamOptions = useMemo(
    () => ({
      sandboxId,
      containerName,
      sandboxGroupId,
      command: '/bin/bash',
      environment: {
        TERM: 'xterm-256color',
        LANG: 'C.UTF-8',
        LC_ALL: 'C.UTF-8',
        TZ: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      onOutput: handleOutput,
      onSessionId: handleSessionId,
      onExit: handleExit,
      onError: handleError,
      onStateChange: handleStateChange,
      onReconnecting: handleReconnecting,
      onReconnectFailed: handleReconnectFailed,
    }),
    [
      sandboxId,
      containerName,
      sandboxGroupId,
      handleOutput,
      handleSessionId,
      handleExit,
      handleError,
      handleStateChange,
      handleReconnecting,
      handleReconnectFailed,
    ]
  );

  // Set up exec stream hook
  const execStream = useExecStream(execStreamOptions);

  // Store execStream in ref for stable access in effects
  useEffect(() => {
    execStreamRef.current = execStream;
  }, [execStream]);

  // Initialize xterm.js terminal - only once per component lifetime
  useEffect(() => {
    if (!terminalRef.current || isInitializedRef.current) return;

    isInitializedRef.current = true;
    console.log(`[TerminalPanel:${panelKey}] Initializing terminal`);

    // Create terminal instance with proper settings
    const terminal = new Terminal({
      cursorBlink: true,
      fontSize: 14,
      fontFamily:
        '"Cascadia Code", "Cascadia Mono", "Fira Code", "JetBrains Mono", "MesloLGS NF", Menlo, Monaco, "Courier New", monospace',
      theme: {
        background: '#1e1e1e',
        foreground: '#d4d4d4',
        cursor: '#d4d4d4',
        cursorAccent: '#1e1e1e',
        selectionBackground: '#264f78',
        selectionForeground: '#ffffff',
        black: '#000000',
        red: '#cd3131',
        green: '#0dbc79',
        yellow: '#e5e510',
        blue: '#2472c8',
        magenta: '#bc3fbc',
        cyan: '#11a8cd',
        white: '#e5e5e5',
        brightBlack: '#868686',
        brightRed: '#f14c4c',
        brightGreen: '#23d18b',
        brightYellow: '#f5f543',
        brightBlue: '#3b8eea',
        brightMagenta: '#d670d6',
        brightCyan: '#29b8db',
        brightWhite: '#e5e5e5',
      },
      allowTransparency: false,
      convertEol: false,
      scrollback: 10000,
    });

    // Create fit addon for auto-sizing
    const fitAddon = new FitAddon();
    terminal.loadAddon(fitAddon);

    // Mount terminal to DOM
    terminal.open(terminalRef.current);

    // Store refs
    xtermRef.current = terminal;
    fitAddonRef.current = fitAddon;

    // Intercept keyboard shortcuts using shared handler
    terminal.attachCustomKeyEventHandler(
      createTerminalKeyHandler({
        terminal,
        sendInput: (data) => execStreamRef.current?.sendInput(data),
        isConnected: () => connectionStateRef.current === 'connected',
        readOnly: false,
      })
    );

    // Display welcome message
    terminal.writeln('\x1b[1;32mSandbox Terminal\x1b[0m');
    terminal.writeln('\x1b[90mConnecting to shell...\x1b[0m');
    terminal.writeln('');

    // Handle terminal input - send each keystroke to the exec stream
    const dataDisposable = terminal.onData((data) => {
      if (connectionStateRef.current === 'connected' && execStreamRef.current) {
        execStreamRef.current.sendInput(data);
      }
    });

    // Handle terminal resize - notify server of new dimensions
    const resizeDisposable = terminal.onResize(({ rows, cols }) => {
      if (connectionStateRef.current === 'connected' && execStreamRef.current) {
        execStreamRef.current.resize(rows, cols);
      }
    });

    // Handle window resize
    const handleResize = () => {
      fitAddon.fit();
    };
    window.addEventListener('resize', handleResize);

    // Use ResizeObserver for more accurate container size tracking
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        if (fitAddonRef.current) {
          fitAddonRef.current.fit();
        }
      });
    });
    if (terminalRef.current) {
      resizeObserver.observe(terminalRef.current);
    }

    // Initial fit and connect with a small delay to ensure DOM is ready
    requestAnimationFrame(() => {
      fitAddon.fit();
      // After fit, connect with the actual dimensions
      if (connectionStateRef.current === 'disconnected' && execStreamRef.current) {
        const rows = terminal.rows;
        const cols = terminal.cols;
        console.log(`[TerminalPanel:${panelKey}] Auto-connecting with dimensions: ${rows}x${cols}`);
        execStreamRef.current.connect(rows, cols);
      }
    });

    // Cleanup on unmount
    return () => {
      console.log(`[TerminalPanel:${panelKey}] Disposing terminal`);
      dataDisposable.dispose();
      resizeDisposable.dispose();
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();
      if (execStreamRef.current) {
        execStreamRef.current.disconnect();
      }
      terminal.dispose();
      xtermRef.current = null;
      fitAddonRef.current = null;
      isInitializedRef.current = false;
    };
    // Only run on mount/unmount - we use refs for everything else
  }, [panelKey]);

  // Register terminal content getter for copy functionality
  useEffect(() => {
    const getContent = () => getTerminalContent(xtermRef.current);

    registerTerminalContentGetter(tabId, panelType, getContent);

    return () => {
      unregisterTerminalContentGetter(tabId, panelType);
    };
  }, [tabId, panelType, registerTerminalContentGetter, unregisterTerminalContentGetter]);

  // Update stdin enabled/disabled based on connection state
  useEffect(() => {
    if (xtermRef.current) {
      const shouldDisable = panelConnectionState !== 'connected';
      xtermRef.current.options.disableStdin = shouldDisable;
      console.log(
        `[TerminalPanel:${panelKey}] stdin ${shouldDisable ? 'disabled' : 'enabled'} (state: ${panelConnectionState})`
      );
    }
  }, [panelConnectionState, panelKey]);

  // Refit and focus terminal when it becomes active
  useEffect(() => {
    if (isActive && xtermRef.current && fitAddonRef.current) {
      const timeoutId = setTimeout(() => {
        fitAddonRef.current?.fit();
        xtermRef.current?.focus();
      }, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [isActive]);

  // Handle manual reconnection when user clicks the Reconnect button
  const handleManualReconnect = useCallback(() => {
    if (xtermRef.current) {
      xtermRef.current.writeln('\r\n\x1b[33m[Reconnecting...]\x1b[0m');
    }
    // Use current terminal dimensions for reconnect
    const rows = xtermRef.current?.rows ?? 24;
    const cols = xtermRef.current?.cols ?? 80;
    execStream.connect(rows, cols);
  }, [execStream]);

  return (
    <div
      className="relative h-full w-full overflow-hidden"
      style={{ backgroundColor: '#1e1e1e', padding: '8px' }}
    >
      <div ref={terminalRef} className="h-full w-full" />

      {/* Reconnect button overlay when connection failed */}
      {panelConnectionState === 'error' && (
        <div className="absolute bottom-4 right-4">
          <button
            onClick={handleManualReconnect}
            className="flex items-center gap-2 rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          >
            <RefreshCw className="h-4 w-4" />
            Reconnect
          </button>
        </div>
      )}
    </div>
  );
}
